import { useCallback, useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuthenticateBySecretMutation } from '@app/api/Authentication';
import {
  ACCESS_RIGHTS_PROP,
  ACCESS_TOKEN,
  AUTHORIZATION_PATH,
  ERROR,
} from '@app/constants';
import { NotificationContext } from '@app/providers';
import type { NNotificationProvider } from '@app/providers/NotificationProvider/NotificationProvider.types';
import {
  setAccessRights,
  setOrganizations,
  setUser,
  useAppDispatch,
} from '@app/store';
import type { NAuth } from '@app/types';

import { adaptUserToRedux } from '@pages/Authorization/utils/adaptUserToRedux';
import { persistAuthorizationAccessToken } from '@pages/Authorization/utils/authorizationSession';

const SECRET_PARAM = 'secret';

export const useAuthorizationSecret = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { showNotification } = useContext(NotificationContext);
  const [authenticateBySecret] = useAuthenticateBySecretMutation();

  const secret = useMemo(
    () => String(searchParams.get(SECRET_PARAM) || ''),
    [searchParams]
  );

  const authorizeBySecretHandler = useCallback(async () => {
    if (!secret) {
      return;
    }

    try {
      const data = await authenticateBySecret({ [SECRET_PARAM]: secret });

      if ('error' in data) {
        if ('data' in data.error) {
          showNotification(
            data.error.data,
            data.error.status as NNotificationProvider.NotifyType
          );
          navigate(AUTHORIZATION_PATH);
        }

        return;
      }

      if (data?.data) {
        const user: NAuth.ModelAuthentication = data.data;

        dispatch(setUser(adaptUserToRedux(user)));
        dispatch(setAccessRights(user[ACCESS_RIGHTS_PROP]));
        dispatch(setOrganizations(user.Organizations));
        persistAuthorizationAccessToken(user[ACCESS_TOKEN]);
        navigate('/');
      }
    } catch (error) {
      showNotification((error as Error).message, ERROR);
      navigate(AUTHORIZATION_PATH);
    }
  }, [authenticateBySecret, dispatch, navigate, secret, showNotification]);

  return {
    authorizeBySecretHandler,
    isSecret: Boolean(secret),
  };
};
